import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { Topbar } from "@/components/Topbar";
import { usePathname } from "@/lib/next-compat";
import { useTenant } from "@/lib/tenant";

/**
 * Sidebar dibentuk dari /api/v1/me/navigation — sudah tersaring di server
 * berdasarkan entitlement paket DAN permission pengguna. Tidak ada lagi daftar
 * menu hardcoded di sini, dan tidak ada lagi nama tenant yang ditulis literal.
 */
const KUNCI_SIDEBAR = "avio.sidebar";
const SEMPIT = "(max-width: 1024px)";

/** Baca preferensi tersimpan. null = pengguna belum pernah memilih, ikuti layar. */
function preferensi(): boolean | null {
  try {
    const v = localStorage.getItem(KUNCI_SIDEBAR);
    return v === "ciut" ? true : v === "lebar" ? false : null;
  } catch { return null; }
}

export default function ALayout() {
  const path = usePathname();
  const { tenant, navigation, status } = useTenant();

  // Default mengikuti lebar layar; begitu pengguna menekan burger, pilihannya
  // menang dan disimpan. Inilah alasan lipatan dipindah dari media query ke JS —
  // CSS tidak bisa membiarkan pengguna menimpa keputusan berbasis lebar layar.
  const [ciut, setCiut] = useState<boolean>(() => {
    const p = preferensi();
    if (p !== null) return p;
    try { return window.matchMedia(SEMPIT).matches; } catch { return false; }
  });

  // Ikuti perubahan lebar layar HANYA selama pengguna belum memilih sendiri.
  useEffect(() => {
    if (preferensi() !== null) return;
    let mq: MediaQueryList;
    try { mq = window.matchMedia(SEMPIT); } catch { return; }
    const ubah = (e: MediaQueryListEvent) => { if (preferensi() === null) setCiut(e.matches); };
    mq.addEventListener("change", ubah);
    return () => mq.removeEventListener("change", ubah);
  }, []);

  const alih = () => {
    setCiut((v) => {
      const baru = !v;
      try { localStorage.setItem(KUNCI_SIDEBAR, baru ? "ciut" : "lebar"); } catch { /* mode privat: cukup berlaku sesi ini */ }
      return baru;
    });
  };

  return (
    <div className="avio-shell" data-ciut={ciut ? "true" : undefined}>
      <aside className="avio-sidebar" id="avio-sidebar">
        <div className="avio-sidebar__brand" style={{ padding: "var(--space-7) var(--space-6) var(--space-5)", borderBottom: "1px solid var(--color-border)" }}>
          <BrandLogo url={tenant?.logoUrl ?? null} nama={tenant?.name ?? "AVIO"} />
          <div className="avio-t-sm avio-muted avio-sidebar__brand-meta" style={{ marginTop: 2 }}>
            {tenant ? tenant.name : status === "loading" ? "memuat…" : "—"}
          </div>
          {tenant?.subscription && (
            <div className="avio-t-xs avio-muted avio-sidebar__brand-meta" style={{ marginTop: 2 }}>
              {tenant.subscription.packageName}
              {tenant.subscription.isDemo && " · demo"}
            </div>
          )}
        </div>

        <nav className="avio-nav">
          {status === "loading" && (
            <div style={{ padding: "var(--space-5) var(--space-6)", display: "grid", gap: 8 }}>
              {[0, 1, 2, 3, 4].map((i) => <div key={i} className="avio-skeleton" style={{ height: 10 }} />)}
            </div>
          )}
          {status === "ready" && navigation.length === 0 && (
            <div className="avio-t-sm avio-muted" style={{ padding: "var(--space-6)" }}>
              Tidak ada menu yang aktif untuk paket langganan tenant ini.
              Hubungi administrator AVIO.
            </div>
          )}
          {navigation.map((g) => (
            <div key={g.key}>
              <div className="avio-nav-section">{g.label}</div>
              {g.items.map((it) => (
                <a key={it.path} href={it.path} className="avio-nav-item" title={it.label} aria-current={path?.startsWith(it.path) ? "page" : undefined}>
                  <i className={`fa-solid ${it.icon ?? "fa-circle"} avio-nav-item__icon`} />
                  <span className="avio-nav-item__label">{it.label}</span>
                </a>
              ))}
            </div>
          ))}
        </nav>

        <div className="avio-t-xs avio-muted avio-sidebar__foot" style={{ marginTop: "auto", padding: "var(--space-6) var(--space-5)" }}>
          {tenant?.subdomain ? `${tenant.subdomain}.avio.id` : "avio.id"}<br />PT Activa Digital Indonesia
        </div>
      </aside>
      <div style={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
        <Topbar ciut={ciut} onAlihSidebar={alih} />
        <div className="avio-main"><Outlet /></div>
      </div>
    </div>
  );
}

/**
 * Slot brand sidebar. Logo TENANT menang bila ada — itulah inti whitelabel, dan
 * memang yang dinyatakan design-system v65 (tokens.css: "di tenant view logo AVIO
 * tidak tampil"). Logo AVIO hanya cadangan saat tenant belum mengunggah apa pun.
 *
 * Kegagalan memuat ditangani eksplisit: kalau berkas di penyimpanan hilang,
 * gambar rusak jauh lebih buruk daripada kembali ke logo AVIO.
 */
function BrandLogo({ url, nama }: { url: string | null; nama: string }) {
  const [gagal, setGagal] = useState(false);
  useEffect(() => { setGagal(false); }, [url]);
  const pakaiTenant = Boolean(url) && !gagal;
  return (
    <img
      src={pakaiTenant ? (url as string) : "/logo-avio.png"}
      alt={pakaiTenant ? nama : "AVIO"}
      onError={() => setGagal(true)}
      className="avio-sidebar__logo"
    />
  );
}
